import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box, Paper, Typography, Grid, Autocomplete, TextField, Button, CircularProgress, Alert, useTheme,
} from '@mui/material';
import { DonutChart, LineChart } from '@personal-dashboard/ui';
import { GET_EXERCISES, GET_EXERCISE_LOGS } from '../queries';
import dayjs from 'dayjs';

export const GymAnalytics: React.FC = () => {
  const theme = useTheme();
  const { data: exData, loading: exLoading, error: exError } = useQuery(GET_EXERCISES);
  const [selectedEx, setSelectedEx] = useState<any>(null);

  const { data: logsData, loading } = useQuery(GET_EXERCISE_LOGS, {
    variables: selectedEx ? { exerciseId: selectedEx.id } : {},
    fetchPolicy: 'network-only',
  });

  const exercises = exData?.getExercises || [];
  const logs = logsData?.getExerciseLogs || [];

  if (exLoading) return <CircularProgress />;
  if (exError) return <Alert severity="error">Failed to load exercises: {exError.message}</Alert>;

  if (!selectedEx) {
    const categoryCounts: { [key: string]: number } = {
      CHEST: 0, BACK: 0, LEGS: 0, SHOULDERS: 0, ARMS: 0, CARDIO: 0, CORE: 0, PLYOMETRICS: 0,
    };

    logs.forEach((log: any) => {
      const cat = log.exercise?.category;
      if (cat && cat in categoryCounts) {
        const completedSets = log.sets?.filter((s: any) => s.completed).length || 0;
        categoryCounts[cat] += completedSets;
      }
    });

    const donutData = Object.keys(categoryCounts)
      .map(cat => ({ name: cat, value: categoryCounts[cat] }))
      .filter(item => item.value > 0);

    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Gym Analytics</Typography>
        <Box sx={{ mb: 3, maxWidth: 400 }}>
          <Autocomplete
            options={exercises}
            getOptionLabel={(o) => `${o.name} (${o.category})`}
            value={selectedEx}
            onChange={(_, val) => setSelectedEx(val)}
            renderInput={(params) => <TextField {...params} label="Select Exercise for History" size="small" />}
          />
        </Box>
        {donutData.length === 0 ? (
          <Typography color="text.secondary">No sets logged yet. Log some workouts to see distribution.</Typography>
        ) : (
          <Box sx={{ height: 350 }}>
            <DonutChart
              title="Completed Sets by Muscle Group"
              seriesName="Sets Completed"
              data={donutData}
              height={320}
            />
          </Box>
        )}
      </Paper>
    );
  }

  if (loading) {
    return <Typography sx={{ p: 2 }}>Loading analytics...</Typography>;
  }

  // Build gap-free timeline
  const logsByDate: { [key: string]: any[] } = {};
  logs.forEach((log: any) => {
    const dateKey = dayjs(log.date).format('MM/DD');
    if (!logsByDate[dateKey]) {
      logsByDate[dateKey] = [];
    }
    logsByDate[dateKey].push(log);
  });

  const sortedDates = Object.keys(logsByDate).sort();
  const startDate = sortedDates.length > 0 ? dayjs(sortedDates[0], 'MM/DD') : dayjs();
  const endDate = sortedDates.length > 0 ? dayjs(sortedDates[sortedDates.length - 1], 'MM/DD') : dayjs();
  const dayCount = endDate.diff(startDate, 'day') + 1;

  const fullDates = Array.from({ length: dayCount }, (_, i) => startDate.add(i, 'day').format('MM/DD'));

  const oneRmMap: Record<string, number> = {};
  const volumeMap: Record<string, number> = {};

  sortedDates.forEach(date => {
    const dayLogs = logsByDate[date];
    let maxOneRm = 0;
    let totalVol = 0;

    dayLogs.forEach((log: any) => {
      log.sets.forEach((set: any) => {
        if (set.completed) {
          totalVol += set.weight * set.reps;
          const oneRm = set.reps === 1 ? set.weight : set.weight * (1 + set.reps / 30);
          if (oneRm > maxOneRm) {
            maxOneRm = oneRm;
          }
        }
      });
    });

    oneRmMap[date] = Math.round(maxOneRm * 10) / 10;
    volumeMap[date] = totalVol;
  });

  const continuousOneRm = fullDates.map((date) => oneRmMap[date] ?? 0);
  const continuousVolume = fullDates.map((date) => volumeMap[date] ?? 0);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Exercise Performance Trend</Typography>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Autocomplete
          options={exercises}
          getOptionLabel={(o) => `${o.name} (${o.category})`}
          value={selectedEx}
          onChange={(_, val) => setSelectedEx(val)}
          renderInput={(params) => <TextField {...params} label="Select Exercise for History" size="small" />}
          sx={{ width: 300 }}
        />
        <Button size="small" onClick={() => setSelectedEx(null)}>Clear Selection</Button>
      </Box>

      {fullDates.length === 0 ? (
        <Typography color="text.secondary">No logs found for this exercise.</Typography>
      ) : (
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ height: 320 }}>
              <LineChart
                title="Estimated 1-Rep Max Progress (kg)"
                categories={fullDates}
                series={[{ name: 'Est. 1RM', data: continuousOneRm, color: theme.palette.primary.main }]}
                height={280}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ height: 320 }}>
              <LineChart
                title="Total Training Volume (kg)"
                categories={fullDates}
                series={[{ name: 'Volume', data: continuousVolume, color: theme.palette.success.main }]}
                height={280}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};
